import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BookOpen, ExternalLink, Loader2, X } from 'lucide-react';
import { loadAdminGuideSection, openAdminGuide } from '../lib/guide';

/**
 * Ekranın sağından açılan kullanım kılavuzu çekmecesi.
 *
 * `guideSection` verilirse içerik kılavuzun kendisidir
 * (`backend/docs/admin_guide.html`): çekmece o ekranın bölümünü oturumlu
 * uçtan okur, sandbox'lı bir iframe'e basar ve `children` bölümün üstünde
 * çizilir (ör. kurulumun o anki durumu). Verilmezse gövde yalnızca
 * `children`'dır: ekran işini kendi diliyle anlatır, kılavuzun tamamı alttaki
 * düğmeyle açılır.
 *
 * Pencere portal ile `document.body` altına taşınır, bu yüzden tema
 * değişkenleri `.admin-portal-theme` ile yeniden verilir (ConfirmDialog'daki
 * gerekçenin aynısı). Escape ve arka plan tıklaması kapatır, odak çekmecede
 * döner, kapanınca açan düğmeye geri gider.
 */
const HelpDrawer = ({
  open, title, subtitle, guideSection, onClose, children,
}) => {
  const titleId = useId();
  const closeRef = useRef(null);
  const drawerRef = useRef(null);
  const returnRef = useRef(null);
  // Effect yalnızca açılış/kapanışta çalışır: `onClose` bağımlılık olsaydı
  // satır içi verilen fonksiyon her render'da odağı kapatma düğmesine çekerdi.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [doc, setDoc] = useState({ state: 'idle', html: '' });

  useEffect(() => {
    if (!open) return undefined;
    returnRef.current = document.activeElement;
    closeRef.current?.focus();
    // Arkadaki sayfa çekmeceyle birlikte kaymasın.
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !drawerRef.current) return;
      // Odak çekmecede döner; arkadaki sayfaya geçmez (aria-modal).
      const items = drawerRef.current.querySelectorAll(
        'a[href], button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])',
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      returnRef.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open || !guideSection) return undefined;
    let alive = true;
    setDoc((current) => (current.html ? current : { state: 'loading', html: '' }));
    loadAdminGuideSection(guideSection)
      .then((html) => {
        if (alive) setDoc(html ? { state: 'ready', html } : { state: 'missing', html: '' });
      })
      .catch(() => {
        if (alive) setDoc({ state: 'error', html: '' });
      });
    return () => { alive = false; };
  }, [open, guideSection]);

  if (!open) return null;

  return createPortal((
    <div className="admin-help-layer admin-portal-theme" role="presentation">
      <button
        type="button"
        className="admin-help-backdrop"
        aria-label="Kılavuzu kapat"
        tabIndex={-1}
        onClick={onClose}
      />
      <aside
        ref={drawerRef}
        className="admin-help-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="admin-help-head">
          <span className="admin-help-icon" aria-hidden="true">
            <BookOpen size={18} />
          </span>
          <div className="min-w-0">
            <h2 id={titleId}>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button
            ref={closeRef}
            type="button"
            className="icon-btn view admin-help-close"
            onClick={onClose}
            aria-label="Kılavuzu kapat"
          >
            <X size={16} />
          </button>
        </header>

        {/* Kılavuz bölümü yoksa çekmecenin gövdesi ekranın kendi
            anlatımıdır ve kaydırılabilir alanı o doldurur. */}
        {children && (
          <div className={guideSection ? 'admin-help-intro' : 'admin-help-body'}>
            {children}
          </div>
        )}

        {guideSection && (
          <div className="admin-help-doc">
            {doc.state === 'ready' && (
              // Betik ve aynı köken izni yok: kılavuz yönetici oturumuna erişemez.
              <iframe
                title={`${title} — kullanım kılavuzu`}
                sandbox=""
                srcDoc={doc.html}
              />
            )}
            {(doc.state === 'loading' || doc.state === 'idle') && (
              <div className="admin-help-state" role="status">
                <Loader2 size={20} className="animate-spin" aria-hidden="true" />
                Kılavuz yükleniyor…
              </div>
            )}
            {(doc.state === 'error' || doc.state === 'missing') && (
              <div className="admin-help-state" role="alert">
                {doc.state === 'error'
                  ? 'Kılavuz alınamadı. Bağlantınızı kontrol edip yeniden açın.'
                  : 'Bu ekranın kılavuz bölümü bulunamadı.'}
              </div>
            )}
          </div>
        )}

        <footer className="admin-help-foot">
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={() => openAdminGuide()}
          >
            <ExternalLink size={14} aria-hidden="true" /> Kılavuzun tamamını aç
          </button>
        </footer>
      </aside>
    </div>
  ), document.body);
};

export default HelpDrawer;
